import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiDownload } from 'react-icons/fi';
import { useAuthStore } from '../../stores';

import tradePilotLogo from '../../assets/copy-image.png';

interface ShareOrderModalProps {
  order: any;
  onClose: () => void;
}

const ShareOrderModal: React.FC<ShareOrderModalProps> = ({ order, onClose }) => {
  const { user } = useAuthStore();
  const shareCardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);

  // 1. ROBUST IMAGE LOADING
  useEffect(() => {
    let active = true;
    const convertImage = async () => {
      try {
        const response = await fetch(tradePilotLogo);
        if (!response.ok) throw new Error('Image not found');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (active) setBase64Image(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        console.warn("Decorative image could not be loaded, card will generate without it.");
      }
    };
    convertImage();
    return () => { active = false; };
  }, []);

  const generateCanvas = async (element: HTMLElement, includeImage: boolean) => {
    // @ts-ignore
    return await window.html2canvas(element, {
      backgroundColor: '#0f172a',
      scale: 2,
      useCORS: true,
      allowTaint: true, // Allow it to try, we handle errors below
      logging: false,
      width: 500, // Enforce width matches container width
      onclone: (doc: any) => {
        const el = doc.querySelector('.share-card-container') as HTMLElement;
        if (el) {
          // Clean up CSS for the screenshot engine
          el.style.transform = 'none';
          el.style.boxShadow = 'none';
          el.style.margin = '0';
        }

        // If we are in fallback mode, remove the image from the clone entirely
        if (!includeImage) {
          const img = doc.querySelector('.decorative-image');
          if (img) img.remove();
        }
      }
    });
  };

  const handleDownload = async () => {
    if (!shareCardRef.current || isGenerating) return;
    setIsGenerating(true);

    try {
      // @ts-ignore
      if (typeof window.html2canvas === 'undefined') {
        throw new Error('Generator loading...');
      }

      // Small delay to ensure DOM is painted
      await new Promise(r => setTimeout(r, 200));

      let canvas;

      try {
        // ATTEMPT 1: Try to render everything perfectly with the image
        canvas = await generateCanvas(shareCardRef.current, true);
      } catch (firstError) {
        console.warn("Standard generation failed, retrying in safe mode...", firstError);

        // ATTEMPT 2: FAIL-SAFE
        // If the specific 'createPattern' or '0x0' error occurs, 
        // we regenerate WITHOUT the decorative image so the user still gets their data.
        canvas = await generateCanvas(shareCardRef.current, false);
      }

      // Process the result
      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `trade-pilot-${order.symbol}-${Date.now()}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        }
        setIsGenerating(false);
      }, 'image/png');

    } catch (error) {
      console.error('Fatal generation error:', error);
      alert('Unable to generate image. Please try again.');
      setIsGenerating(false);
    }
  };

  const formatCurrency = (value: number | string) => {
    return `$${parseFloat(value?.toString() || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return 'N/A'; }
  };

  // Safe data extraction
  const qty = parseFloat(order.qty || '0');
  const filledQty = parseFloat(order.filled_qty || order.qty || '0');
  const avgFillPrice = parseFloat(order.filled_avg_price || order.limit_price || order.stop_price || '0');
  const totalValue = avgFillPrice * filledQty;
  const pnl = order.pnl || 0;
  const pnlPercent = order.pnl_percent || 0;
  const isProfit = pnl >= 0;
  const side = order.side?.toLowerCase();
  const isBuy = side === 'buy';

  // Status Logic
  const status = (order.status || '').toLowerCase();
  const isPending = ['pending', 'new', 'submitted', 'pending_new', 'queued', 'trigger_pending'].includes(status);
  const isCancelled = ['cancelled', 'canceled', 'expired', 'rejected', 'failed'].includes(status);
  const isFilled = !isPending && !isCancelled;

  // Theme Helpers
  let themeColor = isProfit ? '#10b981' : '#ef4444';
  let themeBg = isProfit ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)';
  let themeBlur = isProfit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)';

  if (isPending) {
    themeColor = '#f59e0b'; // Amber
    themeBg = 'rgba(245, 158, 11, 0.15)';
    themeBlur = 'rgba(245, 158, 11, 0.2)';
  } else if (isCancelled) {
    themeColor = '#ef4444'; // Red
    themeBg = 'rgba(239, 68, 68, 0.15)';
    themeBlur = 'rgba(239, 68, 68, 0.2)';
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-3 sm:p-4">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] sm:max-h-[99vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-border">
          <h3 className="text-xl sm:text-2xl font-bold text-foreground min-w-0 break-anywhere">Share Order - {order.symbol}</h3>
          <button onClick={onClose} className="p-2 hover:bg-surface-elevated rounded-lg transition-colors shrink-0">
            <FiX className="h-6 w-6 text-muted" />
          </button>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-4 p-4 sm:p-6 border-t border-border">
          <button onClick={onClose} className="btn-secondary w-full sm:w-auto" disabled={isGenerating}>Cancel</button>
          <button
            onClick={handleDownload}
            disabled={isGenerating}
            className={`btn-primary flex items-center justify-center gap-2 w-full sm:w-auto ${isGenerating ? 'opacity-50' : ''}`}
          >
            {isGenerating ? <span className="animate-pulse">Generating...</span> : <><FiDownload className="h-5 w-5" /><span>Download Image</span></>}
          </button>
        </div>

        {/* Share Card Wrapper */}
        <div className="p-3 sm:p-6 flex justify-start sm:justify-center overflow-x-auto bg-black/20">
          <div
            ref={shareCardRef}
            className="share-card-container"
            style={{
              width: '500px',
              minWidth: '500px', // Enforce width for canvas
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
              padding: '0',
              borderRadius: '20px',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden',
              position: 'relative',
              border: '1px solid rgba(51, 65, 85, 1)'
            }}
          >
            {/* Background Gradient */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '200px',
              background: `radial-gradient(ellipse at top, ${themeBg} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}></div>

            {/* Header */}
            <div style={{ padding: '24px 32px 16px', position: 'relative', zIndex: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#ffffff', marginBottom: '4px' }}>Trade Pilot</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500' }}>Trading Report</div>
                </div>
                <div style={{
                  padding: '6px 12px',
                  background: isBuy ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  borderRadius: '10px',
                  border: isBuy ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: isBuy ? '#10b981' : '#ef4444', textTransform: 'uppercase', position: 'relative', top: !isGenerating ? '0px' : '-7px' }}>{order.side}</div>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1', marginBottom: '6px' }}>{order.symbol}</div>
                {avgFillPrice > 0 && (
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#e2e8f0' }}>{formatCurrency(avgFillPrice)}</div>
                )}
              </div>

              <div style={{
                display: 'inline-block', padding: '5px 12px', background: 'rgba(100, 116, 139, 0.3)',
                borderRadius: '8px', fontSize: '10px', fontWeight: '600', color: '#cbd5e1', textTransform: 'uppercase',
              }}>
                <p style={{ position: 'relative', top: !isGenerating ? '0px' : '-7px' }}>{order.type?.toUpperCase() || 'MARKET'} ORDER</p>
              </div>
            </div>

            {/* PnL Card */}
            {/* Status / PnL Card */}
            {((pnl !== 0 || pnlPercent !== 0) || isPending || isCancelled) && (
              <div style={{
                margin: '0 32px 20px',
                padding: '20px 24px',
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(71, 85, 105, 0.3)',
                position: 'relative',
                zIndex: 2,
              }}>
                {/* Decorative inner gradient */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'linear-gradient(180deg, rgba(148, 163, 184, 0.03) 0%, transparent 100%)',
                  borderRadius: '16px', pointerEvents: 'none',
                }}></div>

                <div style={{ position: 'relative', zIndex: 2 }}>
                  {isPending ? (
                    <>
                      <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600', marginBottom: '12px', textAlign: 'center' }}>
                        Current Status
                      </div>
                      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '32px', fontWeight: '700', lineHeight: '1', color: themeColor, letterSpacing: '-1px' }}>
                          PENDING
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#94a3b8', marginBottom: '12px' }}>
                        Awaiting Execution
                      </div>
                    </>
                  ) : isCancelled ? (
                    <>
                      <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600', marginBottom: '12px', textAlign: 'center' }}>
                        Final Status
                      </div>
                      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '32px', fontWeight: '700', lineHeight: '1', color: themeColor, letterSpacing: '-1px' }}>
                          {status.toUpperCase()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#94a3b8', marginBottom: '12px' }}>
                        Order Not Filled
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600', marginBottom: '12px', textAlign: 'center' }}>
                        {isProfit ? "Total Profit" : "Total Loss"}
                      </div>
                      {/* <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        <div style={{ fontSize: '32px', fontWeight: '700', lineHeight: '1', color: isProfit ? '#10b981' : '#ef4444', letterSpacing: '-2px' }}>
                          {pnlPercent !== 0 ? `${pnlPercent > 0 ? '+' : ''}${pnlPercent.toFixed(2)}%` : '-'}
                        </div>
                      </div> */}
                      <div style={{ textAlign: 'center', fontSize: '32px', fontWeight: '700', color: isProfit ? '#10b981' : '#ef4444', marginBottom: '12px' }}>
                        {pnl !== 0 ? `${pnl > 0 ? '+' : ''}${formatCurrency(Math.abs(pnl))}` : '-'}
                      </div>
                      <div style={{ height: '1px', background: 'rgba(71, 85, 105, 0.5)', margin: '12px 0' }}></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500', textTransform: 'uppercase' }}>Total Value</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>{formatCurrency(totalValue)}</div>
                      </div>
                    </>
                  )}

                </div>
              </div>
            )}

            {/* Visual Image Section */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px 32px', position: 'relative', zIndex: 1 }}>
              <div style={{ position: 'relative', width: '100%', maxWidth: '300px', display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: '320px', height: '150px',
                  background: themeBlur,
                  filter: 'blur(40px)',
                  borderRadius: '50%'
                }}></div>

                {/* 
                  Use Base64. If it's ready, show it.
                  Added explicit width/height to img tag to prevent 0x0 errors.
                  Classname 'decorative-image' allows us to remove it if it crashes capture.
                */}
                {base64Image ? (
                  <div style={{ borderRadius: '10px', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
                    <img
                      src={base64Image}
                      alt="Trade Pilot"
                      className="decorative-image"
                      width="300"
                      height="225"
                      style={{
                        width: '100%', height: 'auto', opacity: 0.9,
                        display: 'block', verticalAlign: 'top',
                        filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))'
                      }}
                    />
                  </div>
                ) : (
                  // Spacer so layout doesn't collapse while loading
                  <div style={{ height: '150px', width: '100%' }}></div>
                )}
              </div>
            </div>

            {/* Details Grid */}
            <div style={{ margin: '0 32px 20px', padding: '20px', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '14px', border: '1px solid rgba(71, 85, 105, 0.3)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Quantity</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>{qty.toFixed(0)}</div>
                  {/* <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>shares</div> */}
                </div>
                {isFilled && (
                  <div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Filled</div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>{filledQty.toFixed(0)}</div>
                    {/* <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>shares</div> */}
                  </div>
                )}
                {avgFillPrice > 0 && (
                  <div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                      {isFilled ? 'Avg Fill Price' : 'Order Price'}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>{formatCurrency(avgFillPrice)}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Order Type</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>{order.type?.toUpperCase() || 'MARKET'}</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 32px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(71, 85, 105, 0.3)' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600', marginBottom: '2px' }}>{user?.name || 'Trader'}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{formatDateTime(order.submittedAt || order.created_at || order.submitted_at)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', color: '#ffffff', fontWeight: '700', marginBottom: '2px' }}>Trade Pilot</div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>www.thetradepilot.ai</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareOrderModal;